import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Icon,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Theme,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import useBoolean from "hooks/useBoolean";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { getIconsFontawesome } from "./FontAwesomeIcons";
import { useQuery } from "react-query";

type IconPickerProps = {
  value?: string;
  onChange: (value?: string) => void;
};

const useStyles = (theme: Theme) => ({
  icon: {
    width: "1.5em",
  },
  selectedIcon: {
    color: theme.palette.secondary.main,
    width: "1.5em",
  },
  selectedButtonWrapper: {
    float: "right",
    border: `1px solid #DCDCDC`,
    borderRadius: "3px",
    padding: "6px",
  },
  paginationButtonGroup: {
    float: "right",
  },
  paper: {
    p: 1,
    border: "1px solid #DCDCDC",
  },
  searchBox: {
    flexGrow: 1,
    marginRight: 1,
  },
});

const PAGE_SIZE = 15 * 6; /*items per line x lines */

const IconPicker = ({ value, onChange }: IconPickerProps) => {
  const styles = useStyles(useTheme());
  const [icon, setIcon] = useState<string | undefined>(value);
  const [searchTerm, setSearchTerm] = useState("");
  const { formatMessage: t } = useIntl();
  const [pageNumMax, setPageNumMax] = useState(1);
  const [icons, setIcons] = useState<any[]>([]);

  const [searching, setSearching] = useBoolean(false);
  const [page, setPage] = useState(1);

  const { data: totalIconsList = [], isFetching, isFetched } = useQuery(
    ["getIconsFontawesome"],
    () => getIconsFontawesome()
  );

  useEffect(() => {
    if (totalIconsList.length) {
      setIcons(totalIconsList.slice(0, PAGE_SIZE));
      setPageNumMax(1 + Math.trunc(totalIconsList.length / PAGE_SIZE));
    }
  }, [totalIconsList]);

  useEffect(() => {
    let pageNumber = page - 1;
    let itemIndex = pageNumber * PAGE_SIZE;
    let itemCount = (pageNumber + 1) * PAGE_SIZE;

    if (searchTerm.trim() !== "") {
      setSearching.setTrue();
      let filteredIcons = totalIconsList.filter((icon) =>
        icon.includes(searchTerm)
      );
      setPageNumMax(1 + Math.trunc(filteredIcons.length / PAGE_SIZE));

      setIcons(filteredIcons.slice(itemIndex, itemCount));
      setSearching.setFalse();
    } else {
      const newIconsPage = totalIconsList.slice(itemIndex, itemCount);
      setIcons(newIconsPage);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const iconChange = (value?: string | undefined) => {
    setIcon(value);
    onChange(value);
  };

  return (
    <>
      {isFetched && (
        <Paper elevation={0}>
          <Box mb={1} display="flex">
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t({ id: "searchIconsPlaceholder" })}
              value={searchTerm ?? ""}
              sx={styles.searchBox}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchTerm("");
                        setIcons(totalIconsList.slice(0, PAGE_SIZE));
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e: any) => {
                setSearching.setTrue();
                setSearchTerm(e.target.value);
                let filteredIcons = totalIconsList.filter((icon) =>
                  icon.includes(e.target.value)
                );
                setIcons(filteredIcons.slice(0, PAGE_SIZE));
                setPageNumMax(1 + Math.trunc(filteredIcons.length / PAGE_SIZE));
                setPage(1);

                setSearching.setFalse();
              }}
            />
            <Tooltip title={icon ?? ""}>
              <IconButton
                color="primary"
                size="medium"
                component="span"
                sx={styles.selectedButtonWrapper}
                onClick={() => iconChange(undefined)}
              >
                <Icon
                  fontSize="large"
                  className={icon}
                  sx={styles.selectedIcon}
                />
              </IconButton>
            </Tooltip>
          </Box>
          <Paper sx={styles.paper}>
            <ButtonGroup
              size="large"
              style={{
                display: "flex",
                flexFlow: "row wrap",
              }}
            >
              {!searching ? (
                icons.map((icon, index) => {
                  return (
                    <IconButton
                      key={index}
                      color="primary"
                      size="medium"
                      component="span"
                      title={icon}
                      onClick={(e: any) => {
                        iconChange(icons[index]);
                      }}
                    >
                      <Icon
                        className={icon}
                        fontSize="large"
                        sx={styles.icon}
                      />
                    </IconButton>
                  );
                })
              ) : (
                <CircularProgress size={24} />
              )}
            </ButtonGroup>
          </Paper>
          <Box m={1}>
            <ButtonGroup sx={styles.paginationButtonGroup} size="large">
              <Button
                disabled={page === 1}
                onClick={() => setPage((prev) => prev - 1)}
              >
                Previous Page
              </Button>
              <Button
                disabled={page >= pageNumMax}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Next Page
              </Button>
            </ButtonGroup>
          </Box>
        </Paper>
      )}
      {isFetching && <CircularProgress size={36} />}
    </>
  );
};

export default IconPicker;
